/* eslint-disable react/prop-types */
import { useMemo, useState } from 'react';
import { useQueryState, parseAsString } from 'nuqs';
import {
  ArrowUpFromLine,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clipboard,
  CreditCard,
  KeyRound,
  Link as LinkIcon,
  RefreshCcw,
  Server,
  ShieldCheck,
  UserRound,
  Webhook,
  Repeat,
} from 'lucide-react';
import { toast } from 'sonner';

import MerchantLayout from '@/shared/components/merchant/MerchantLayout';
import { useAuth } from '@/shared/auth/AuthContext';
import {
  methodClass,
  CodeBlock,
  SectionHeader,
  FieldsTable,
  EndpointDetails,
  EndpointList,
} from './DocumentationPrimitives';
import {
  flowapaySections,
  flowapayEndpointDefaults,
  buildFlowapayExamples,
  FlowapaySectionContent,
} from './flowapayDocs';

const FALLBACK_BASE_URL = 'https://server.vexgate.com';

const endpointDefaults = {
  auth: 'Secret End Merchant API key',
  surface: 'Merchant application server-side integration',
  version: 'v1',
  includeInLeftNav: true,
};

const nativeSections = [
  {
    id: 'overview',
    title: 'Overview',
    icon: BookOpen,
    summary: 'Shared setup guidance for backend payment creation, frontend checkout, and async status handling.',
  },
  {
    id: 'authentication',
    title: 'Authentication',
    icon: KeyRound,
    summary: 'How secret API keys, publishable keys, client secrets, and dashboard JWTs differ.',
  },
  {
    id: 'payments',
    title: 'Payments',
    icon: CreditCard,
    summary: 'Create, retrieve, confirm, capture, and cancel payments from your backend.',
    endpoints: [
      {
        id: 'create-payment',
        title: 'Create payment',
        method: 'POST',
        path: '/payments',
        description: 'Create a payment intent and receive a client_secret for checkout.',
        requestFields: [
          ['amount', 'integer', 'Required. Amount in minor units, for example 6540 for USD 65.40.'],
          ['currency', 'string', 'Required. ISO 4217 currency code.'],
          ['profile_id', 'string', 'Required. Merchant profile that owns the payment.'],
          ['customer_id', 'string', 'Optional. Reuse your customer ID for returning customers and saved methods.'],
          ['return_url', 'string', 'Optional. Where the customer returns after redirect or wallet flows. If not provided, the Return URL configured on the business profile is used. If neither is set, the request will fail.'],
          ['metadata', 'object', 'Optional order, cart, or reconciliation data.'],
        ],
        responseFields: [
          ['payment_id', 'string', 'Store this against your order.'],
          ['status', 'string', 'Current payment state.'],
          ['client_secret', 'string', 'Pass to the browser SDK for this payment only.'],
          ['amount', 'integer', "The customer's order amount, in minor units."],
          ['net_amount', 'integer', 'The total for the merchant, in minor units — amount plus any appended transaction fees configured on the profile. Equal to amount when no appended fees apply.'],
          ['transaction_fee_breakdown', 'array', 'Fees configured on the profile as applied to this payment. Each entry has label (string), type ("fixed" or "percent"), value (integer — minor units for fixed, for example 150 = 1.50 in the payment currency; basis points for percent, for example 250 = 2.50%), and calculation ("include" — already part of amount; "append" — added on top, reflected in net_amount). Absent when the profile has no fees.'],
        ],
        notes: [
          'Create payments from your backend. Never expose a secret API key in browser or mobile code.',
          'Use an idempotent order workflow on your side so a retry does not create duplicate orders.',
          'Transaction fees are configured on your profile. Included fees are already part of `amount`; appended fees are added on top and show up in `net_amount`.',
        ],
      },
      {
        id: 'retrieve-payment',
        title: 'Retrieve payment',
        method: 'GET',
        path: '/payments/{payment_id}',
        description: 'Retrieve payment status after redirect, webhook delivery, or support lookup.',
        requestFields: [['payment_id', 'path string', 'Payment ID returned from POST /payments.']],
        responseFields: [
          ['payment_id', 'string', 'Payment identifier.'],
          ['status', 'string', 'Latest payment state.'],
          ['amount', 'integer', "The customer's order amount, in minor units."],
          ['net_amount', 'integer', 'The total for the merchant, in minor units — amount plus any appended transaction fees configured on the profile. Equal to amount when no appended fees apply.'],
          ['transaction_fee_breakdown', 'array', 'Fees configured on the profile as applied to this payment. Each entry has label (string), type ("fixed" or "percent"), value (integer — minor units for fixed, for example 150 = 1.50 in the payment currency; basis points for percent, for example 250 = 2.50%), and calculation ("include" — already part of amount; "append" — added on top, reflected in net_amount). Absent when the profile has no fees.'],
          ['metadata', 'object', 'Metadata supplied at payment creation.'],
        ],
        notes: [
          'Use webhooks for asynchronous state changes; use retrieve as a server-side reconciliation read.',
          'Transaction fees are configured on your profile. Included fees are already part of `amount`; appended fees are added on top and show up in `net_amount`.',
        ],
      },
      {
        id: 'confirm-payment',
        title: 'Confirm payment',
        method: 'POST',
        path: '/payments/{payment_id}/confirm',
        description: 'Confirm a payment when your integration is not relying on hosted checkout confirmation.',
        requestFields: [
          ['payment_method', 'string', 'Optional payment method family, such as card.'],
          ['payment_method_type', 'string', 'Optional type, such as credit or debit.'],
          ['payment_token', 'string', 'Optional saved payment method token.'],
          ['customer_acceptance', 'object', 'Required for some saved-method or mandate flows.'],
        ],
        responseFields: [
          ['payment_id', 'string', 'Payment identifier.'],
          ['status', 'string', 'Updated payment state.'],
          ['next_action', 'object', 'Redirect or challenge instruction if customer action is required.'],
        ],
        notes: ['Most checkout integrations create the payment server-side and let HyperLoader handle confirmation.'],
      },
      {
        id: 'capture-payment',
        title: 'Capture payment',
        method: 'POST',
        path: '/payments/{payment_id}/capture',
        description: 'Capture a manually authorized payment.',
        requestFields: [['amount_to_capture', 'integer', 'Optional partial capture amount in minor units.']],
        responseFields: [['status', 'string', 'Updated payment state after capture.']],
        notes: ['Use this only for payments created with manual capture behavior.'],
      },
      {
        id: 'cancel-payment',
        title: 'Cancel payment',
        method: 'POST',
        path: '/payments/{payment_id}/cancel',
        description: 'Cancel a payment before it is fully captured or finalized.',
        requestFields: [['cancellation_reason', 'string', 'Optional reason recorded with the payment.']],
        responseFields: [['status', 'string', 'Updated cancellation state.']],
        notes: ['Cancellation availability depends on the payment status and payment method.'],
      },
    ],
  },
  {
    id: 'checkout',
    title: 'Checkout',
    icon: Server,
    summary: 'Hosted or embedded checkout flow using backend payment creation and frontend client_secret rendering.',
  },
  {
    id: 'payment-links',
    title: 'Payment Links',
    icon: LinkIcon,
    summary: 'Create hosted links from payments and retrieve link state.',
    endpoints: [
      {
        id: 'create-payment-link',
        title: 'Create payment link',
        method: 'POST',
        path: '/payments',
        description: 'Create a payment with payment_link=true to generate a hosted payment link.',
        requestFields: [
          ['amount', 'integer', 'Required amount in minor units.'],
          ['currency', 'string', 'Required ISO currency code.'],
          ['profile_id', 'string', 'Required merchant profile.'],
          ['payment_link', 'boolean', 'Set to true.'],
          ['return_url', 'string', 'Optional. Return URL after the hosted flow. Falls back to the business profile Return URL if not provided.'],
        ],
        responseFields: [
          ['payment_id', 'string', 'Payment backing the link.'],
          ['payment_link', 'object', 'Hosted payment link details.'],
          ['status', 'string', 'Current payment state.'],
        ],
        notes: ['Payment link styling and allowed methods come from dashboard configuration.'],
      },
      {
        id: 'list-payment-links',
        title: 'List payment links',
        method: 'POST',
        path: '/payment_link/list',
        description: 'List payment links created for your profile.',
        requestFields: [
          ['limit', 'integer', 'Optional page size.'],
          ['offset', 'integer', 'Optional pagination offset.'],
          ['created_after', 'timestamp', 'Optional lower created-at bound.'],
          ['created_before', 'timestamp', 'Optional upper created-at bound.'],
        ],
        responseFields: [['data', 'array', 'Payment link records for the current profile.']],
        notes: ['Use this for backend reconciliation; use the portal for day-to-day operations.'],
      },
      {
        id: 'retrieve-payment-link',
        title: 'Retrieve payment link',
        method: 'GET',
        path: '/payment_link/{payment_link_id}',
        description: 'Retrieve a payment link by ID.',
        requestFields: [['payment_link_id', 'path string', 'Payment link identifier.']],
        responseFields: [['payment_link_details', 'object', 'Hosted link and related payment details.']],
        notes: ['Useful when your system stores payment_link_id separately from payment_id.'],
      },
    ],
  },
  {
    id: 'customers',
    title: 'Customers',
    icon: UserRound,
    summary: 'Create customer records and retrieve saved payment methods for returning customers.',
    endpoints: [
      {
        id: 'create-customer',
        title: 'Create customer',
        method: 'POST',
        path: '/customers',
        description: 'Create a customer record for repeat payments and saved payment methods.',
        requestFields: [
          ['customer_id', 'string', 'Optional merchant-defined customer ID.'],
          ['name', 'string', 'Optional customer name.'],
          ['email', 'string', 'Optional customer email.'],
          ['phone', 'string', 'Optional phone number.'],
          ['metadata', 'object', 'Optional customer metadata.'],
        ],
        responseFields: [['customer_id', 'string', 'Customer ID to reuse on future payments.']],
        notes: ['Send the same customer_id on future payments to display eligible saved methods in checkout.'],
      },
      {
        id: 'retrieve-customer',
        title: 'Retrieve customer',
        method: 'GET',
        path: '/customers/{customer_id}',
        description: 'Retrieve customer details.',
        requestFields: [['customer_id', 'path string', 'Customer ID.']],
        responseFields: [
          ['customer_id', 'string', 'Customer identifier.'],
          ['email', 'string', 'Customer email.'],
          ['metadata', 'object', 'Customer metadata.'],
        ],
        notes: ['Use this to sync support, CRM, or order-management views.'],
      },
      {
        id: 'update-customer',
        title: 'Update customer',
        method: 'POST',
        path: '/customers/{customer_id}',
        description: 'Update customer contact details or metadata on the v1 customer surface.',
        requestFields: [
          ['name', 'string', 'Optional replacement name.'],
          ['email', 'string', 'Optional replacement email.'],
          ['phone', 'string', 'Optional replacement phone.'],
          ['metadata', 'object', 'Optional replacement metadata.'],
        ],
        responseFields: [['customer_id', 'string', 'Updated customer identifier.']],
        notes: ['This v1 route uses POST.'],
      },
      {
        id: 'delete-customer',
        title: 'Delete customer',
        method: 'DELETE',
        path: '/customers/{customer_id}',
        description: 'Delete a customer record.',
        requestFields: [['customer_id', 'path string', 'Customer ID.']],
        responseFields: [['customer_id', 'string', 'Deleted customer identifier.']],
        notes: ['Deleting a customer does not rewrite your order history. Keep your own audit records as needed.'],
      },
      {
        id: 'list-customer-payment-methods',
        title: 'List customer payment methods',
        method: 'GET',
        path: '/customers/{customer_id}/payment_methods',
        description: 'List saved payment methods for a customer on the v1 customer surface.',
        requestFields: [['customer_id', 'path string', 'Customer ID.']],
        responseFields: [['payment_methods', 'array', 'Saved cards, wallets, or reusable methods available for the customer.']],
        notes: ['Saved methods require prior customer consent and successful setup in an earlier flow.'],
      },
    ],
  },
  {
    id: 'refunds',
    title: 'Refunds',
    icon: RefreshCcw,
    summary: 'Create refunds, retrieve refund state, and optionally update refund metadata.',
    endpoints: [
      {
        id: 'create-refund',
        title: 'Create refund',
        method: 'POST',
        path: '/refunds',
        description: 'Create a full or partial refund against a payment.',
        requestFields: [
          ['payment_id', 'string', 'Required payment to refund.'],
          ['amount', 'integer', 'Optional amount in minor units. Omit for full refund.'],
          ['reason', 'string', 'Optional support or customer-facing reason.'],
          ['metadata', 'object', 'Optional reconciliation data.'],
        ],
        responseFields: [
          ['refund_id', 'string', 'Store this for support and reconciliation.'],
          ['status', 'string', 'Refund status.'],
        ],
        notes: ['Partial refunds cannot exceed the captured amount that remains refundable.'],
      },
      {
        id: 'retrieve-refund',
        title: 'Retrieve refund',
        method: 'GET',
        path: '/refunds/{refund_id}',
        description: 'Retrieve refund status.',
        requestFields: [['refund_id', 'path string', 'Refund ID.']],
        responseFields: [
          ['refund_id', 'string', 'Refund identifier.'],
          ['status', 'string', 'Latest refund status.'],
        ],
        notes: ['Webhook delivery is the preferred way to track asynchronous status changes.'],
      },
      {
        id: 'update-refund',
        title: 'Update refund metadata',
        method: 'POST',
        path: '/refunds/{refund_id}',
        description: 'Update refund metadata.',
        requestFields: [['metadata', 'object', 'Metadata to attach to the refund.']],
        responseFields: [
          ['refund_id', 'string', 'Refund identifier.'],
          ['metadata', 'object', 'Updated metadata.'],
        ],
        notes: ['Use metadata for support ticket IDs or internal refund categories.'],
      },
    ],
  },
  {
    id: 'payouts',
    title: 'Payouts',
    icon: ArrowUpFromLine,
    summary: 'Create, retrieve, update, confirm, cancel, and fulfill payouts.',
    endpoints: [
      {
        id: 'create-payout',
        title: 'Create payout',
        method: 'POST',
        path: '/payouts/create',
        description: 'Initiate a payout.',
        requestFields: [
          ['amount', 'integer', 'Required payout amount in minor units.'],
          ['currency', 'string', 'Required ISO currency code.'],
          ['profile_id', 'string', 'Required merchant profile.'],
          ['customer_id', 'string', 'Optional beneficiary/customer reference.'],
        ],
        responseFields: [
          ['payout_id', 'string', 'Payout identifier.'],
          ['status', 'string', 'Payout status.'],
        ],
        notes: ['Required beneficiary fields depend on your enabled payout methods.'],
      },
      {
        id: 'retrieve-payout',
        title: 'Retrieve payout',
        method: 'GET',
        path: '/payouts/{payout_id}',
        description: 'Retrieve payout status.',
        requestFields: [['payout_id', 'path string', 'Payout ID.']],
        responseFields: [
          ['payout_id', 'string', 'Payout identifier.'],
          ['status', 'string', 'Latest payout status.'],
        ],
        notes: ['Use this for support lookup and reconciliation.'],
      },
      {
        id: 'update-payout',
        title: 'Update payout',
        method: 'PUT',
        path: '/payouts/{payout_id}',
        description: 'Update payout details before fulfillment.',
        requestFields: [['metadata', 'object', 'Optional metadata or supported mutable payout fields.']],
        responseFields: [['payout_id', 'string', 'Updated payout identifier.']],
        notes: ['Fields that can be changed depend on the payout status and rail.'],
      },
      {
        id: 'confirm-payout',
        title: 'Confirm payout',
        method: 'POST',
        path: '/payouts/{payout_id}/confirm',
        description: 'Confirm a payout when your payout flow separates creation from confirmation.',
        requestFields: [['payout_id', 'path string', 'Payout ID.']],
        responseFields: [['status', 'string', 'Updated payout status.']],
        notes: ['Use only if your payout flow requires explicit confirmation.'],
      },
      {
        id: 'cancel-payout',
        title: 'Cancel payout',
        method: 'POST',
        path: '/payouts/{payout_id}/cancel',
        description: 'Cancel a payout before it is finalized.',
        requestFields: [['payout_id', 'path string', 'Payout ID.']],
        responseFields: [['status', 'string', 'Updated payout status.']],
        notes: ['Cancellation availability depends on payout status.'],
      },
      {
        id: 'fulfill-payout',
        title: 'Fulfill payout',
        method: 'POST',
        path: '/payouts/{payout_id}/fulfill',
        description: 'Mark or trigger payout fulfillment for flows that require a separate fulfillment step.',
        requestFields: [['payout_id', 'path string', 'Payout ID.']],
        responseFields: [['status', 'string', 'Updated payout status.']],
        notes: ['Use only if this step is enabled for your payout integration.'],
      },
    ],
  },
  {
    id: 'subscriptions',
    title: 'Subscriptions',
    icon: Repeat,
    summary: 'Create, confirm, update, pause, resume, cancel, list, and estimate subscriptions.',
    endpoints: [
      {
        id: 'create-confirm-subscription',
        title: 'Create and confirm subscription',
        method: 'POST',
        path: '/subscriptions',
        description: 'Create and confirm a subscription in one server-side call.',
        requestFields: [
          ['customer_id', 'string', 'Required customer reference.'],
          ['profile_id', 'string', 'Required merchant profile.'],
          ['plan_id', 'string', 'Plan or product reference configured for subscription billing.'],
        ],
        responseFields: [
          ['subscription_id', 'string', 'Subscription identifier.'],
          ['status', 'string', 'Subscription status.'],
        ],
        notes: ['Use this when the initial payment and subscription confirmation happen together.'],
      },
      {
        id: 'create-subscription',
        title: 'Create subscription',
        method: 'POST',
        path: '/subscriptions/create',
        description: 'Create a subscription before explicit confirmation.',
        requestFields: [
          ['customer_id', 'string', 'Required customer reference.'],
          ['profile_id', 'string', 'Required merchant profile.'],
          ['plan_id', 'string', 'Plan or product reference configured for subscription billing.'],
        ],
        responseFields: [['subscription_id', 'string', 'Subscription identifier.']],
        notes: ['Pair with POST /subscriptions/{subscription_id}/confirm when your flow requires review before confirmation.'],
      },
      {
        id: 'list-subscription-items',
        title: 'List subscription items',
        method: 'GET',
        path: '/subscriptions/items',
        description: 'List subscription items.',
        requestFields: [['subscription_id', 'query string', 'Optional subscription filter when supported.']],
        responseFields: [['items', 'array', 'Subscription items.']],
        notes: ['Use this for subscription item reconciliation.'],
      },
      {
        id: 'list-subscriptions',
        title: 'List subscriptions',
        method: 'GET',
        path: '/subscriptions/list',
        description: 'List subscriptions for reconciliation or support.',
        requestFields: [
          ['limit', 'integer', 'Optional page size.'],
          ['offset', 'integer', 'Optional pagination offset.'],
        ],
        responseFields: [['data', 'array', 'Subscription records.']],
        notes: ['Avoid building dashboard-like operational views into your integration unless needed.'],
      },
      {
        id: 'retrieve-subscription',
        title: 'Retrieve subscription',
        method: 'GET',
        path: '/subscriptions/{subscription_id}',
        description: 'Retrieve subscription details.',
        requestFields: [['subscription_id', 'path string', 'Subscription ID.']],
        responseFields: [['subscription_id', 'string', 'Subscription identifier.'], ['status', 'string', 'Latest subscription status.']],
        notes: ['Use this after redirects, payment failures, or webhook delivery.'],
      },
      {
        id: 'confirm-subscription',
        title: 'Confirm subscription',
        method: 'POST',
        path: '/subscriptions/{subscription_id}/confirm',
        description: 'Confirm a previously created subscription.',
        requestFields: [['subscription_id', 'path string', 'Subscription ID.']],
        responseFields: [['status', 'string', 'Updated subscription status.']],
        notes: ['Use after POST /subscriptions/create when your flow separates creation and confirmation.'],
      },
      {
        id: 'update-subscription',
        title: 'Update subscription',
        method: 'PUT',
        path: '/subscriptions/{subscription_id}/update',
        description: 'Update mutable subscription fields.',
        requestFields: [['metadata', 'object', 'Optional metadata or supported mutable subscription fields.']],
        responseFields: [['subscription_id', 'string', 'Updated subscription identifier.']],
        notes: ['Use the v1 PUT update route shown here.'],
      },
      {
        id: 'pause-subscription',
        title: 'Pause subscription',
        method: 'POST',
        path: '/subscriptions/{subscription_id}/pause',
        description: 'Pause an active subscription.',
        requestFields: [['subscription_id', 'path string', 'Subscription ID.']],
        responseFields: [['status', 'string', 'Updated subscription status.']],
        notes: ['Pause behavior depends on billing-cycle rules configured for the subscription.'],
      },
      {
        id: 'resume-subscription',
        title: 'Resume subscription',
        method: 'POST',
        path: '/subscriptions/{subscription_id}/resume',
        description: 'Resume a paused subscription.',
        requestFields: [['subscription_id', 'path string', 'Subscription ID.']],
        responseFields: [['status', 'string', 'Updated subscription status.']],
        notes: ['Use this after a deliberate pause, not as payment failure recovery unless your product flow requires it.'],
      },
      {
        id: 'cancel-subscription',
        title: 'Cancel subscription',
        method: 'POST',
        path: '/subscriptions/{subscription_id}/cancel',
        description: 'Cancel a subscription.',
        requestFields: [['subscription_id', 'path string', 'Subscription ID.']],
        responseFields: [['status', 'string', 'Updated subscription status.']],
        notes: ['Persist cancellation reason in metadata or your own system if needed for support.'],
      },
      {
        id: 'estimate-subscription',
        title: 'Estimate subscription',
        method: 'GET',
        path: '/subscriptions/estimate',
        description: 'Estimate subscription pricing before creation or update.',
        requestFields: [['customer_id', 'query string', 'Optional customer reference when estimate depends on customer context.']],
        responseFields: [['estimate', 'object', 'Estimated amount, currency, taxes, and line items when available.']],
        notes: ['Use estimates to present pricing before committing to a subscription change.'],
      },
    ],
  },
  {
    id: 'webhooks',
    title: 'Webhooks',
    icon: Webhook,
    summary: 'Configure a dashboard webhook endpoint and process asynchronous integration events on your server.',
  },
];

function flattenEndpoints(sections, defaults) {
  return sections.flatMap((section) =>
    (section.endpoints || []).map((endpoint) => ({
      ...defaults,
      ...endpoint,
      group: section.title,
      sectionId: section.id,
    }))
  );
}

const nativeEndpoints = flattenEndpoints(nativeSections, endpointDefaults);
const flowapayEndpoints = flattenEndpoints(flowapaySections, flowapayEndpointDefaults);

const SURFACES = {
  native: {
    id: 'native',
    label: 'Native API',
    sections: nativeSections,
    endpoints: nativeEndpoints,
    defaultSectionId: 'overview',
    tagline:
      'Practical v1 API guidance for the merchant application backend and checkout frontend. Dashboard administration APIs are intentionally excluded.',
  },
  'u-compat': {
    id: 'u-compat',
    label: 'U-Compat API',
    sections: flowapaySections,
    endpoints: flowapayEndpoints,
    defaultSectionId: 'fp-overview',
    tagline:
      'Drop-in surface for merchants migrating from an existing gateway. Same request and response shapes, served at /api/v1 with a profile-bound Bearer key. Available only on profiles switched to the U-Compat contract.',
  },
};

const webhookEventGroups = [
  {
    label: 'Payment',
    contentType: 'payment_details',
    note: 'Transaction fees are configured on your profile. Included fees are already part of `amount`; appended fees are added on top and show up in `net_amount`.',
    events: [
      'payment_succeeded',
      'payment_failed',
      'payment_processing',
      'payment_cancelled',
      'payment_cancelled_post_capture',
      'payment_authorized',
      'payment_partially_authorized',
      'payment_captured',
      'payment_expired',
      'action_required',
    ],
    examplePayload: `{
  "merchant_id": "merchant_1776960717",
  "event_id": "evt_01J...",
  "event_type": "payment_succeeded",
  "content": {
    "type": "payment_details",
    "object": {
      "payment_id": "pay_mbabizu24mvu3mela5njyhpit4",
      "merchant_id": "merchant_1776960717",
      "status": "succeeded",
      "amount": 6540,
      "net_amount": 6690,
      "transaction_fee_breakdown": [
        {
          "label": "Gateway fee",
          "type": "percent",
          "value": 300,
          "calculation": "include"
        },
        {
          "label": "Service fee",
          "type": "fixed",
          "value": 150,
          "calculation": "append"
        }
      ],
      "amount_capturable": 0,
      "amount_received": 6540,
      "connector": "stripe",
      "client_secret": "pay_mbabizu24mvu3mela5njyhpit4_secret_xxx",
      "created": "2026-05-13T10:30:00Z",
      "modified_at": "2026-05-13T10:30:05Z",
      "currency": "USD",
      "customer_id": "customer_123",
      "description": "Order #1001",
      "payment_method": "card",
      "payment_method_data": {
        "card": {
          "last4": "4242",
          "card_network": "Visa",
          "card_type": "credit"
        }
      },
      "capture_method": "automatic",
      "shipping": null,
      "billing": null,
      "metadata": { "order_id": "1001" },
      "error": null,
      "refunds": null,
      "disputes": null,
      "mandate_id": null,
      "setup_future_usage": null,
      "off_session": null,
      "return_url": "https://example.com/checkout/complete",
      "profile_id": "pro_igFZ0LUAdZNqGkGfGmLN"
    }
  },
  "timestamp": "2026-05-13T10:30:05Z"
}`,
    objectFields: [
      ['payment_id', 'string', 'Unique payment identifier.'],
      ['status', 'IntentStatus', 'Current payment state (e.g. succeeded, failed, processing).'],
      ['amount', 'integer', "The customer's order amount, in minor units."],
      ['net_amount', 'integer', 'The total for the merchant, in minor units — amount plus any appended transaction fees configured on the profile. Equal to amount when no appended fees apply.'],
      ['transaction_fee_breakdown', 'array', 'Fees configured on the profile as applied to this payment. Each entry has label (string), type ("fixed" or "percent"), value (integer — minor units for fixed, for example 150 = 1.50 in the payment currency; basis points for percent, for example 250 = 2.50%), and calculation ("include" — already part of amount; "append" — added on top, reflected in net_amount). Absent when the profile has no fees.'],
      ['amount_capturable', 'integer', 'Remaining capturable amount (0 when fully captured).'],
      ['amount_received', 'integer | null', 'Total captured amount.'],
      ['currency', 'string', 'ISO 4217 currency code.'],
      ['customer_id', 'string | null', 'Customer identifier.'],
      ['connector', 'string | null', 'Payment connector that processed this payment.'],
      ['payment_method', 'string | null', 'Payment method family (card, bank_transfer, etc).'],
      ['payment_method_data', 'object | null', 'Payment method details including card last4, network.'],
      ['capture_method', 'string | null', 'automatic or manual.'],
      ['error', 'object | null', 'Error details if the payment failed.'],
      ['metadata', 'object | null', 'Metadata supplied at payment creation.'],
      ['return_url', 'string | null', 'Customer return URL.'],
      ['created', 'timestamp', 'Payment creation time.'],
      ['modified_at', 'timestamp', 'Last modification time.'],
      ['profile_id', 'string', 'Business profile identifier.'],
    ],
  },
  {
    label: 'Refund',
    contentType: 'refund_details',
    events: ['refund_succeeded', 'refund_failed'],
    examplePayload: `{
  "merchant_id": "merchant_1776960717",
  "event_id": "evt_02J...",
  "event_type": "refund_succeeded",
  "content": {
    "type": "refund_details",
    "object": {
      "refund_id": "ref_mbabizu24mvu3mela5njyhpit4",
      "payment_id": "pay_mbabizu24mvu3mela5njyhpit4",
      "amount": 1500,
      "currency": "USD",
      "status": "succeeded",
      "refund_type": "instant",
      "reason": "Customer returned part of the order",
      "metadata": { "ticket_id": "SUP-42" },
      "error_message": null,
      "error_code": null,
      "created_at": "2026-05-13T11:00:00Z",
      "updated_at": "2026-05-13T11:00:03Z",
      "connector": "stripe",
      "profile_id": "pro_igFZ0LUAdZNqGkGfGmLN",
      "connector_refund_id": "re_3abc123"
    }
  },
  "timestamp": "2026-05-13T11:00:03Z"
}`,
    objectFields: [
      ['refund_id', 'string', 'Unique refund identifier.'],
      ['payment_id', 'string', 'Payment this refund applies to.'],
      ['amount', 'integer', 'Refund amount in minor units.'],
      ['currency', 'string', 'ISO 4217 currency code.'],
      ['status', 'RefundStatus', 'Current refund state (succeeded, failed, pending).'],
      ['refund_type', 'string', 'instant or scheduled.'],
      ['reason', 'string | null', 'Refund reason.'],
      ['metadata', 'object | null', 'Metadata attached to the refund.'],
      ['error_message', 'string | null', 'Error message if failed.'],
      ['error_code', 'string | null', 'Error code if failed.'],
      ['created_at', 'timestamp', 'Refund creation time.'],
      ['updated_at', 'timestamp', 'Last update time.'],
      ['connector', 'string', 'Connector that processed the refund.'],
      ['connector_refund_id', 'string | null', 'Connector-side refund identifier.'],
      ['profile_id', 'string | null', 'Business profile identifier.'],
    ],
  },
  {
    label: 'Dispute',
    contentType: 'dispute_details',
    events: [
      'dispute_opened',
      'dispute_expired',
      'dispute_accepted',
      'dispute_cancelled',
      'dispute_challenged',
      'dispute_won',
      'dispute_lost',
    ],
    examplePayload: `{
  "merchant_id": "merchant_1776960717",
  "event_id": "evt_03J...",
  "event_type": "dispute_opened",
  "content": {
    "type": "dispute_details",
    "object": {
      "dispute_id": "dis_xyz789",
      "payment_id": "pay_mbabizu24mvu3mela5njyhpit4",
      "attempt_id": "att_001",
      "amount": "6540",
      "currency": "USD",
      "dispute_stage": "chargeback",
      "dispute_status": "dispute_opened",
      "connector": "stripe",
      "connector_status": "needs_response",
      "connector_dispute_id": "dp_abc123",
      "connector_reason": "fraudulent",
      "connector_reason_code": "10.4",
      "challenge_required_by": "2026-05-27T23:59:59Z",
      "connector_created_at": "2026-05-13T12:00:00Z",
      "connector_updated_at": "2026-05-13T12:00:00Z",
      "created_at": "2026-05-13T12:00:01Z",
      "profile_id": "pro_igFZ0LUAdZNqGkGfGmLN",
      "is_already_refunded": false
    }
  },
  "timestamp": "2026-05-13T12:00:01Z"
}`,
    objectFields: [
      ['dispute_id', 'string', 'Unique dispute identifier.'],
      ['payment_id', 'string', 'Payment this dispute relates to.'],
      ['attempt_id', 'string', 'Payment attempt identifier.'],
      ['amount', 'string', 'Disputed amount in minor units (string-encoded).'],
      ['currency', 'string', 'ISO 4217 currency code.'],
      ['dispute_stage', 'DisputeStage', 'Stage of the dispute (chargeback, pre_dispute).'],
      ['dispute_status', 'DisputeStatus', 'Current dispute status.'],
      ['connector', 'string', 'Connector that reported the dispute.'],
      ['connector_status', 'string', 'Connector-side dispute status.'],
      ['connector_dispute_id', 'string', 'Connector-side dispute identifier.'],
      ['connector_reason', 'string | null', 'Reason provided by the connector.'],
      ['connector_reason_code', 'string | null', 'Reason code from the connector.'],
      ['challenge_required_by', 'timestamp | null', 'Deadline to respond to the dispute.'],
      ['created_at', 'timestamp', 'When the dispute was received.'],
      ['is_already_refunded', 'boolean', 'Whether the disputed amount was already refunded.'],
    ],
  },
  {
    label: 'Payout',
    contentType: 'payout_details',
    events: [
      'payout_success',
      'payout_failed',
      'payout_initiated',
      'payout_processing',
      'payout_cancelled',
      'payout_expired',
      'payout_reversed',
    ],
    examplePayload: `{
  "merchant_id": "merchant_1776960717",
  "event_id": "evt_04J...",
  "event_type": "payout_success",
  "content": {
    "type": "payout_details",
    "object": {
      "payout_id": "pout_mbabizu24mvu3mela5njyhpit4",
      "merchant_id": "merchant_1776960717",
      "amount": 5000,
      "currency": "USD",
      "connector": "wise",
      "payout_type": "bank",
      "auto_fulfill": true,
      "customer_id": "customer_123",
      "status": "success",
      "error_message": null,
      "error_code": null,
      "profile_id": "pro_igFZ0LUAdZNqGkGfGmLN",
      "created": "2026-05-13T13:00:00Z",
      "description": "Vendor payout for May",
      "metadata": { "batch_id": "batch_42" }
    }
  },
  "timestamp": "2026-05-13T13:00:05Z"
}`,
    objectFields: [
      ['payout_id', 'string', 'Unique payout identifier.'],
      ['amount', 'integer', 'Payout amount in minor units.'],
      ['currency', 'string', 'ISO 4217 currency code.'],
      ['connector', 'string | null', 'Connector that processed the payout.'],
      ['payout_type', 'string | null', 'Payout type (bank, card, wallet).'],
      ['auto_fulfill', 'boolean', 'Whether the payout was auto-fulfilled.'],
      ['customer_id', 'string | null', 'Beneficiary customer identifier.'],
      ['status', 'PayoutStatus', 'Current payout state.'],
      ['error_message', 'string | null', 'Error message if failed.'],
      ['error_code', 'string | null', 'Error code if failed.'],
      ['description', 'string | null', 'Payout description.'],
      ['metadata', 'object | null', 'Payout metadata.'],
      ['profile_id', 'string', 'Business profile identifier.'],
      ['created', 'timestamp', 'Payout creation time.'],
    ],
  },
  {
    label: 'Mandate',
    contentType: 'mandate_details',
    events: ['mandate_active', 'mandate_revoked'],
    examplePayload: `{
  "merchant_id": "merchant_1776960717",
  "event_id": "evt_05J...",
  "event_type": "mandate_active",
  "content": {
    "type": "mandate_details",
    "object": {
      "mandate_id": "man_abcdef123456",
      "status": "active",
      "payment_method_id": "pm_xyz789",
      "payment_method": "card",
      "payment_method_type": "credit",
      "card": {
        "last4_digits": "4242",
        "card_exp_month": "12",
        "card_exp_year": "2028",
        "card_network": "Visa",
        "card_type": "credit",
        "card_issuer": "Chase",
        "card_isin": "424242"
      },
      "customer_acceptance": null
    }
  },
  "timestamp": "2026-05-13T14:00:00Z"
}`,
    objectFields: [
      ['mandate_id', 'string', 'Unique mandate identifier.'],
      ['status', 'MandateStatus', 'Current mandate state (active, revoked).'],
      ['payment_method_id', 'string', 'Saved payment method identifier.'],
      ['payment_method', 'string', 'Payment method family (card).'],
      ['payment_method_type', 'string | null', 'Payment method type (credit, debit).'],
      ['card', 'object | null', 'Card details: last4_digits, card_exp_month, card_exp_year, card_network, card_type, card_issuer, card_isin.'],
      ['customer_acceptance', 'object | null', 'Customer acceptance details if applicable.'],
    ],
  },
  {
    label: 'Subscription',
    contentType: 'subscription_details',
    events: ['invoice_paid'],
    examplePayload: `{
  "merchant_id": "merchant_1776960717",
  "event_id": "evt_06J...",
  "event_type": "invoice_paid",
  "content": {
    "type": "subscription_details",
    "object": {
      "id": "sub_123",
      "merchant_reference_id": "my_sub_ref",
      "status": "active",
      "plan_id": "plan_monthly",
      "item_price_id": "price_monthly_2999",
      "coupon": null,
      "profile_id": "pro_igFZ0LUAdZNqGkGfGmLN",
      "customer_id": "customer_123",
      "payment": {
        "payment_id": "pay_sub_invoice_001",
        "status": "succeeded",
        "amount": 2999
      },
      "invoice": {
        "id": "inv_001",
        "subscription_id": "sub_123",
        "status": "paid"
      },
      "billing_processor_subscription_id": "ext_sub_abc"
    }
  },
  "timestamp": "2026-05-13T15:00:00Z"
}`,
    objectFields: [
      ['id', 'string', 'Unique subscription identifier.'],
      ['merchant_reference_id', 'string | null', 'Your reference for this subscription.'],
      ['status', 'SubscriptionStatus', 'Current subscription state (active, cancelled, paused).'],
      ['plan_id', 'string | null', 'Associated subscription plan.'],
      ['item_price_id', 'string | null', 'Item price identifier.'],
      ['coupon', 'string | null', 'Applied coupon code.'],
      ['profile_id', 'string', 'Business profile identifier.'],
      ['customer_id', 'string | null', 'Customer identifier.'],
      ['payment', 'object | null', 'Payment details for the invoice (payment_id, status, amount).'],
      ['invoice', 'object | null', 'Invoice details (id, subscription_id, status).'],
    ],
  },
];

function DocumentationNav({
  sections,
  selectedSectionId,
  selectedEndpointId,
  expandedSectionIds,
  onToggleSection,
  onSelectSection,
  onSelectEndpoint,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const selectedLabel = sections.find((s) => s.id === selectedSectionId)?.title || 'Overview';

  return (
    <aside aria-label="Documentation navigation" className="lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <button
          type="button"
          onClick={() => setMobileOpen((o) => !o)}
          className="flex w-full items-center justify-between px-2 py-2 text-sm font-semibold text-slate-700 lg:hidden"
        >
          <span>{selectedLabel}</span>
          {mobileOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <nav className={`space-y-1 ${mobileOpen ? '' : 'hidden lg:block'}`}>
          {sections.map((section) => {
            const Icon = section.icon;
            const isExpanded = expandedSectionIds.includes(section.id);
            const isSelected = selectedSectionId === section.id && !selectedEndpointId;
            const navEndpoints = (section.endpoints || []).filter((endpoint) => endpoint.includeInLeftNav !== false);

            return (
              <div key={section.id}>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => { onSelectSection(section.id); setMobileOpen(false); }}
                    className={`flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium transition-colors ${
                      isSelected ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100 hover:text-slate-950'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{section.title}</span>
                  </button>
                  {navEndpoints.length > 0 && (
                    <button
                      type="button"
                      onClick={() => onToggleSection(section.id)}
                      className="rounded-lg px-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${section.title}`}
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  )}
                </div>

                {isExpanded && navEndpoints.length > 0 && (
                  <div className="ml-6 mt-1 space-y-1 border-l border-slate-200 pl-2">
                    {navEndpoints.map((endpoint) => (
                      <button
                        key={endpoint.id}
                        type="button"
                        onClick={() => { onSelectEndpoint(endpoint.id); setMobileOpen(false); }}
                        className={`w-full rounded-lg px-2 py-2 text-left transition-colors ${
                          selectedEndpointId === endpoint.id
                            ? 'bg-cyan-50 text-cyan-800'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-12 rounded border px-1.5 py-0.5 text-center text-[10px] font-bold ${methodClass(endpoint.method)}`}>
                            {endpoint.method}
                          </span>
                          <span className="truncate text-xs font-medium">{endpoint.title}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}

function OverviewSection({ baseUrl, profileId, snippets, onSelectSection }) {
  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950 p-6 text-white sm:p-8">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-300/40 bg-cyan-300/10 px-3 py-1 text-xs font-medium text-cyan-100">
            <BookOpen className="h-3.5 w-3.5" />
            Merchant application integration
          </div>
          <h2 className="max-w-3xl text-3xl font-semibold tracking-tight sm:text-4xl">
            Build your VEXGATE checkout from a clean v1 integration surface.
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
            Your backend creates payments with a secret End Merchant API key. Your frontend renders
            checkout with the publishable key and payment client_secret. Webhooks keep your order
            system synchronized after redirects and asynchronous payment events.
          </p>
        </div>
        <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5">
          {[
            ['Base URL', baseUrl],
            ['profile_id', profileId],
          ].map(([label, value]) => (
            <div key={label} className="flex items-start justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                <code className="mt-2 block break-all text-xs text-slate-900">{value}</code>
              </div>
              <button
                type="button"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(value); toast.success('Copied'); }
                  catch { toast.error('Could not copy'); }
                }}
                className="ml-3 mt-1 shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
                aria-label={`Copy ${label}`}
              >
                <Clipboard className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
        <div className="flex gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
          <div>
            <h2 className="text-sm font-semibold text-amber-950">Keep integration boundaries strict.</h2>
            <p className="mt-1 text-sm leading-6 text-amber-900">
              Secret API keys are server-side only. Publishable keys and client_secret values are
              browser-side inputs for checkout. Dashboard JWTs are for the VEXGATE portal and are
              not application integration credentials.
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={Server}
          title="Recommended integration flow"
          description="Start with the backend payment creation path, then render checkout and reconcile via webhooks."
        />
        <div className="grid gap-3 md:grid-cols-4">
          {[
            ['1', 'Create a secret End Merchant API key in API Keys and store it in your backend secret manager.'],
            ['2', 'Create a payment from your backend with amount, currency, profile_id, customer_id, and return_url.'],
            ['3', 'Load HyperLoader from https://sdk.vexgate.com/HyperLoader.js and mount checkout with the client_secret.'],
            ['4', 'Handle the return URL, retrieve the payment server-side, and rely on webhooks for final status updates.'],
          ].map(([step, text]) => (
            <div key={step} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-3 flex h-7 w-7 items-center justify-center rounded-full bg-slate-950 text-xs font-semibold text-white">
                {step}
              </div>
              <p className="text-sm leading-6 text-slate-700">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <CodeBlock title="create-payment.sh" code={snippets.paymentCreate} />
        <CodeBlock title="checkout.html" code={snippets.checkout} />
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={CheckCircle2}
          title="Production guidance"
          description="These practices matter more than adding every possible endpoint to your integration."
        />
        <div className="grid gap-3 md:grid-cols-2">
          {[
            'Use HTTPS return URLs and webhook URLs.',
            'Store payment_id, refund_id, payout_id, subscription_id, and webhook event_id for reconciliation.',
            'Use stable customer_id values for returning customers and saved methods.',
            'Treat client_secret as payment-scoped checkout input, not as a long-lived credential.',
            'Make order creation idempotent in your own backend before calling payment creation.',
            'Configure webhook endpoint URLs in the dashboard; do not use dashboard event-log APIs as integration APIs.',
          ].map((item) => (
            <div key={item} className="flex gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
              <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-cyan-700" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={BookOpen}
          title="Explore by task"
          description="Endpoint reference is grouped under each integration task in the left navigation."
        />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            ['payments', 'Payments', 'Create and manage payment lifecycle operations.'],
            ['checkout', 'Checkout', 'Render hosted or embedded checkout with HyperLoader.'],
            ['customers', 'Customers', 'Manage customer records and saved methods.'],
            ['webhooks', 'Webhooks', 'Synchronize final state with your order system.'],
          ].map(([id, title, description]) => (
            <button
              key={id}
              type="button"
              onClick={() => onSelectSection(id)}
              className="rounded-xl border border-slate-200 bg-white p-4 text-left transition-colors hover:border-cyan-300 hover:bg-cyan-50"
            >
              <p className="text-sm font-semibold text-slate-950">{title}</p>
              <p className="mt-1 text-xs leading-5 text-slate-600">{description}</p>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function AuthenticationSection() {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <SectionHeader
        icon={KeyRound}
        title="Authentication"
        description="Use the right credential in the right runtime. Most integration bugs here come from crossing those boundaries."
      />
      <div className="grid gap-4 xl:grid-cols-2">
        {[
          ['Secret API key', 'Backend only', 'Send as api-key on server-to-server calls such as POST /payments, POST /refunds, and POST /payouts/create.'],
          ['Publishable key', 'Frontend checkout', 'Use with HyperLoader in browser code. It does not replace the server-side secret API key.'],
        ].map(([title, label, description]) => (
          <div key={title} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-950">{title}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-cyan-700">{label}</p>
            <p className="mt-3 text-sm leading-6 text-slate-700">{description}</p>
          </div>
        ))}
      </div>
      <div className="mt-5">
        <CodeBlock
          title="server-request-header.txt"
          code={`Content-Type: application/json
Accept: application/json
api-key: <YOUR_END_MERCHANT_API_KEY>`}
        />
      </div>
    </section>
  );
}

function CheckoutSection({ snippets }) {
  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={Server}
          title="Checkout"
          description="Checkout has two runtimes: backend payment creation with a secret key, then frontend rendering with the client_secret."
        />
        <div className="grid gap-4 xl:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-950">Backend responsibility</p>
            <p className="mt-2 text-sm leading-6 text-slate-700">
              Create the payment with your secret End Merchant API key. Include amount, currency,
              profile_id, customer_id when available, metadata, and return_url. Store the returned
              payment_id against your order.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-950">Frontend responsibility</p>
            <p className="mt-2 text-sm leading-6 text-slate-700">
              Load HyperLoader from the VEXGATE SDK URL, initialize it with your publishable key,
              and mount checkout with the payment client_secret returned by your backend.
            </p>
          </div>
        </div>
      </section>
      <div className="grid gap-4 xl:grid-cols-2">
        <CodeBlock title="backend-create-payment.sh" code={snippets.paymentCreate} />
        <CodeBlock title="frontend-checkout.html" code={snippets.checkout} />
      </div>
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={RefreshCcw}
          title="Completion and redirects"
          description="A return URL tells the browser where to go. It is not the source of truth for final payment state."
        />
        <p className="text-sm leading-6 text-slate-700">
          After redirect completion, your backend should retrieve the payment by payment_id before
          fulfilling the order. Webhooks should still update your order system because payment methods
          can complete asynchronously after the customer leaves checkout.
        </p>
      </section>
    </div>
  );
}

function WebhookEventExplorer() {
  const [selectedEvent, setSelectedEvent] = useQueryState(
    'event',
    parseAsString.withDefault('payment_succeeded'),
  );
  const defaultCategory = webhookEventGroups.find((g) => g.events.includes(selectedEvent))?.label
    || webhookEventGroups[0]?.label;
  const [expandedCategories, setExpandedCategories] = useState([defaultCategory]);

  const activeGroup = selectedEvent
    ? webhookEventGroups.find((g) => g.events.includes(selectedEvent))
    : null;

  function toggleCategory(label) {
    setExpandedCategories((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  }

  function handleEventClick(event, groupLabel) {
    if (!expandedCategories.includes(groupLabel)) {
      setExpandedCategories((prev) => [...prev, groupLabel]);
    }
    setSelectedEvent(event);
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <SectionHeader
        icon={BookOpen}
        title="Event types and payloads"
        description="Select a category and event to see its payload structure. All events share the same envelope; content.object varies by resource."
      />
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
        {/* Left — category dropdowns */}
        <div className="w-full shrink-0 space-y-1 lg:w-64">
          {webhookEventGroups.map((group) => {
            const isExpanded = expandedCategories.includes(group.label);
            const hasActive = group.events.includes(selectedEvent);
            return (
              <div key={group.label}>
                <button
                  type="button"
                  onClick={() => toggleCategory(group.label)}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                    hasActive
                      ? 'bg-cyan-50 text-cyan-800'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <ChevronRight
                      className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    />
                    {group.label}
                  </span>
                  <code className="rounded bg-slate-200/70 px-1.5 py-0.5 text-[10px] text-slate-500">
                    {group.events.length}
                  </code>
                </button>
                {isExpanded && (
                  <div className="ml-4 mt-0.5 space-y-px border-l border-slate-200 pl-3 pb-1">
                    {group.events.map((event) => (
                      <button
                        key={event}
                        type="button"
                        onClick={() => handleEventClick(event, group.label)}
                        className={`block w-full rounded-md px-2.5 py-1.5 text-left font-mono text-[11px] transition-colors ${
                          selectedEvent === event
                            ? 'bg-cyan-100 font-semibold text-cyan-900'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                      >
                        {event}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right — event detail */}
        <div className="min-w-0 flex-1">
          {activeGroup && selectedEvent ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="rounded-md border border-cyan-200 bg-cyan-50 px-2.5 py-1 font-mono text-xs font-bold text-cyan-800">
                  {selectedEvent}
                </span>
                <span className="text-xs text-slate-500">
                  content.type: <code className="font-semibold text-slate-700">{activeGroup.contentType}</code>
                </span>
              </div>
              <FieldsTable title={`content.object fields (${activeGroup.contentType})`} rows={activeGroup.objectFields} />
              {activeGroup.note && (
                <p className="text-sm leading-6 text-slate-700">{activeGroup.note}</p>
              )}
              <CodeBlock title={`${selectedEvent}.json`} code={activeGroup.examplePayload.replace(/"event_type": "[^"]+"/, `"event_type": "${selectedEvent}"`)} />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-16 text-center text-sm text-slate-400">
              Select an event from the left to view its payload structure
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function WebhooksSection() {
  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={Webhook}
          title="Webhooks"
          description="Receive real-time push notifications when payment state changes. Configure your endpoint in the Webhooks page on the dashboard."
        />
        <div className="space-y-3 text-sm leading-6 text-slate-700">
          <p>
            Set up a dedicated HTTPS endpoint on your server to receive POST requests with
            JSON payloads. Your endpoint must return a <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-900">2xx</code> status
            code to acknowledge receipt. Any non-2xx response or timeout triggers retries.
          </p>
          <p>
            Do not rely on dashboard event listing or retry APIs under /events as merchant
            application APIs. Those are dashboard-only surfaces.
          </p>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={ShieldCheck}
          title="Signature verification"
          description="Every outgoing webhook includes an HMAC signature. Always verify before processing."
        />
        <div className="space-y-3 text-sm leading-6 text-slate-700">
          <p>
            VEXGATE signs every webhook payload using HMAC-SHA512 with your <strong>webhook secret</strong> (available
            in the Config tab on the Webhooks management page). The signature is sent in
            the <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-900">x-webhook-signature-512</code> header.
          </p>
          <p>
            To verify: compute HMAC-SHA512 of the raw request body using your webhook secret, then compare
            the hex digest to the header value.
          </p>
        </div>
        <div className="mt-4">
          <CodeBlock
            title="verify-webhook.js"
            code={`const crypto = require('crypto');

function verifyWebhook(rawBody, signatureHeader, webhookSecret) {
  const expected = crypto
    .createHmac('sha512', webhookSecret)
    .update(rawBody, 'utf8')
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected, 'hex'),
    Buffer.from(signatureHeader, 'hex')
  );
}

// In your webhook handler:
// const signature = req.headers['x-webhook-signature-512'];
// if (!verifyWebhook(req.rawBody, signature, WEBHOOK_SECRET)) {
//   return res.status(401).json({ error: 'Invalid signature' });
// }`}
          />
        </div>
      </section>

      <WebhookEventExplorer />

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={RefreshCcw}
          title="Delivery and retries"
          description="Webhooks are retried over 24 hours if your endpoint does not return 2xx."
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="pb-2 pr-4">Retry</th>
                <th className="pb-2">Interval</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {[
                ['1st', '1 minute'],
                ['2nd – 3rd', '5 minutes'],
                ['4th – 8th', '10 minutes'],
                ['9th – 13th', '1 hour'],
                ['14th – 16th', '6 hours'],
              ].map(([retry, interval]) => (
                <tr key={retry}>
                  <td className="py-2 pr-4 font-medium">{retry}</td>
                  <td className="py-2">{interval}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm leading-6 text-slate-600">
          The first retry interval is measured from the original delivery attempt. Subsequent
          intervals are from the previous retry. Total retry window is approximately 24 hours.
        </p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={CheckCircle2}
          title="Handling duplicates"
          description="Use event_id for idempotent processing."
        />
        <div className="space-y-3 text-sm leading-6 text-slate-700">
          <p>
            Due to retries, your endpoint may receive the same event more than once. Each webhook
            payload includes a unique <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-900">event_id</code>.
          </p>
          <div className="grid gap-3 md:grid-cols-4">
            {[
              ['1', 'Extract event_id from the webhook body.'],
              ['2', 'Check your store (database, Redis) for this event_id.'],
              ['3', 'If unseen, process the event and persist the event_id.'],
              ['4', 'If already seen, return 2xx and skip processing.'],
            ].map(([step, text]) => (
              <div key={step} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 flex h-6 w-6 items-center justify-center rounded-full bg-slate-950 text-xs font-semibold text-white">
                  {step}
                </div>
                <p className="text-xs leading-5 text-slate-700">{text}</p>
              </div>
            ))}
          </div>
          <p>
            Since the last retry happens roughly 24 hours after the original event, keep
            processed event_id records for at least 24 hours before purging.
          </p>
        </div>
      </section>
    </div>
  );
}

function SectionOverview({ section, endpoints, onSelectEndpoint }) {
  const Icon = section.icon;

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader icon={Icon} title={section.title} description={section.summary} />
        <div className="rounded-xl border border-cyan-100 bg-cyan-50 p-4 text-sm leading-6 text-cyan-950">
          These endpoints are documented as v1 merchant application APIs. They require your backend
          to send the secret End Merchant API key and are not dashboard-only JWT surfaces.
        </div>
      </section>
      <EndpointList endpoints={endpoints} onSelectEndpoint={onSelectEndpoint} />
    </div>
  );
}

/**
 * The documentation body — no auth, no merchant chrome. Rendered both by the
 * portal page below (inside MerchantLayout) and by the public docs site
 * (src/apps/docs), which passes no profileId so the placeholder shows.
 */
export function DocumentationContent({ profileId: profileIdProp }) {
  const [surfaceId, setSurfaceId] = useQueryState('api', parseAsString.withDefault('native'));
  const [selectedSectionId, setSelectedSectionId] = useQueryState('section', parseAsString.withDefault('overview'));
  const [selectedEndpointId, setSelectedEndpointId] = useQueryState('endpoint', parseAsString);
  const [expandedSectionIds, setExpandedSectionIds] = useState([]);

  const surface = SURFACES[surfaceId] || SURFACES.native;
  const isFlowapay = surface.id === 'u-compat';

  const baseUrl = (import.meta.env.VITE_API_URL || FALLBACK_BASE_URL).replace(/\/$/, '');
  const profileId = profileIdProp || 'pro_your_profile_id';

  const snippets = useMemo(() => {
    const paymentCreate = `curl --location '${baseUrl}/payments' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "amount": 6540,
    "currency": "USD",
    "profile_id": "${profileId}",
    "customer_id": "customer_123",
    "description": "Order #1001",
    "return_url": "https://example.com/checkout/complete",
    "metadata": {
      "order_id": "1001"
    }
  }'`;

    const checkout = `<script src="https://sdk.vexgate.com/HyperLoader.js"></script>
<div id="unified-checkout"></div>
<script>
  const hyper = window.Hyper("<YOUR_PUBLISHABLE_KEY>", {
    customBackendUrl: "${baseUrl}"
  });

  const widgets = hyper.widgets({
    clientSecret: "<CLIENT_SECRET_FROM_POST_PAYMENTS>",
    appearance: { theme: "midnight" }
  });

  widgets
    .create("payment", {
      layout: "tabs",
      wallets: {
        walletReturnUrl: "https://example.com/checkout/complete"
      }
    })
    .mount("#unified-checkout");
</script>`;

    const webhookPayload = `{
  "event_id": "evt_01J...",
  "type": "payment_succeeded",
  "timestamp": "2026-05-11T10:30:00Z",
  "content": {
    "object": {
      "payment_id": "pay_mbabizu24mvu3mela5njyhpit4",
      "status": "succeeded",
      "amount": 6540,
      "currency": "USD"
    }
  }
}`;

    return { paymentCreate, checkout, webhookPayload };
  }, [baseUrl, profileId]);

  const endpointExamples = useMemo(() => {
    const genericPayment = `{
  "payment_id": "pay_mbabizu24mvu3mela5njyhpit4",
  "status": "succeeded",
  "amount": 6540,
  "currency": "USD",
  "metadata": {
    "order_id": "1001"
  }
}`;
    const genericCustomer = `{
  "customer_id": "customer_123",
  "name": "Jane Merchant",
  "email": "jane@example.com",
  "metadata": {
    "loyalty_tier": "gold"
  }
}`;
    const genericRefund = `{
  "refund_id": "ref_mbabizu24mvu3mela5njyhpit4",
  "payment_id": "pay_mbabizu24mvu3mela5njyhpit4",
  "status": "pending",
  "amount": 1500
}`;
    const genericPayout = `{
  "payout_id": "pout_mbabizu24mvu3mela5njyhpit4",
  "status": "requires_fulfillment",
  "amount": 5000,
  "currency": "USD"
}`;
    const genericSubscription = `{
  "subscription_id": "sub_123",
  "status": "active",
  "customer_id": "customer_123"
}`;

    return {
      'create-payment': {
        request: snippets.paymentCreate,
        response: `{
  "payment_id": "pay_mbabizu24mvu3mela5njyhpit4",
  "status": "requires_payment_method",
  "amount": 6540,
  "currency": "USD",
  "client_secret": "pay_mbabizu24mvu3mela5njyhpit4_secret_xxx",
  "customer_id": "customer_123"
}`,
      },
      'retrieve-payment': {
        request: `curl --location '${baseUrl}/payments/pay_mbabizu24mvu3mela5njyhpit4' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: genericPayment,
      },
      'confirm-payment': {
        request: `curl --location '${baseUrl}/payments/pay_mbabizu24mvu3mela5njyhpit4/confirm' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "payment_method": "card",
    "payment_method_type": "credit",
    "payment_token": "pm_saved_card_token"
  }'`,
        response: genericPayment,
      },
      'capture-payment': {
        request: `curl --location '${baseUrl}/payments/pay_mbabizu24mvu3mela5njyhpit4/capture' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "amount_to_capture": 6540
  }'`,
        response: genericPayment,
      },
      'cancel-payment': {
        request: `curl --location '${baseUrl}/payments/pay_mbabizu24mvu3mela5njyhpit4/cancel' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "cancellation_reason": "requested_by_customer"
  }'`,
        response: `{
  "payment_id": "pay_mbabizu24mvu3mela5njyhpit4",
  "status": "cancelled"
}`,
      },
      'create-payment-link': {
        request: `curl --location '${baseUrl}/payments' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "amount": 6540,
    "currency": "USD",
    "profile_id": "${profileId}",
    "payment_link": true,
    "description": "Order #1001",
    "return_url": "https://example.com/checkout/complete"
  }'`,
        response: `{
  "payment_id": "pay_mbabizu24mvu3mela5njyhpit4",
  "status": "requires_payment_method",
  "payment_link": {
    "link": "${baseUrl}/payment_link/pay_mbabizu24mvu3mela5njyhpit4"
  }
}`,
      },
      'list-payment-links': {
        request: `curl --location '${baseUrl}/payment_link/list' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "limit": 20,
    "offset": 0
  }'`,
        response: `{
  "data": [
    {
      "payment_link_id": "plink_123",
      "payment_id": "pay_mbabizu24mvu3mela5njyhpit4",
      "amount": 6540,
      "currency": "USD"
    }
  ]
}`,
      },
      'retrieve-payment-link': {
        request: `curl --location '${baseUrl}/payment_link/plink_123' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: `{
  "payment_link_id": "plink_123",
  "payment_link_details": {
    "payment_id": "pay_mbabizu24mvu3mela5njyhpit4",
    "amount": 6540,
    "currency": "USD"
  }
}`,
      },
      'create-customer': {
        request: `curl --location '${baseUrl}/customers' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "customer_id": "customer_123",
    "name": "Jane Merchant",
    "email": "jane@example.com",
    "phone": "5550100",
    "phone_country_code": "+1",
    "metadata": {
      "loyalty_tier": "gold"
    }
  }'`,
        response: genericCustomer,
      },
      'retrieve-customer': {
        request: `curl --location '${baseUrl}/customers/customer_123' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: genericCustomer,
      },
      'update-customer': {
        request: `curl --location '${baseUrl}/customers/customer_123' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "email": "new-email@example.com",
    "metadata": {
      "loyalty_tier": "platinum"
    }
  }'`,
        response: genericCustomer,
      },
      'delete-customer': {
        request: `curl --location --request DELETE '${baseUrl}/customers/customer_123' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: `{
  "customer_id": "customer_123",
  "deleted": true
}`,
      },
      'list-customer-payment-methods': {
        request: `curl --location '${baseUrl}/customers/customer_123/payment_methods' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: `{
  "payment_methods": [
    {
      "payment_method_id": "pm_123",
      "payment_method": "card",
      "payment_method_type": "credit",
      "card": {
        "last4": "4242",
        "card_network": "Visa"
      }
    }
  ]
}`,
      },
      'create-refund': {
        request: `curl --location '${baseUrl}/refunds' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "payment_id": "pay_mbabizu24mvu3mela5njyhpit4",
    "amount": 1500,
    "reason": "Customer returned part of the order",
    "metadata": {
      "ticket_id": "SUP-42"
    }
  }'`,
        response: genericRefund,
      },
      'retrieve-refund': {
        request: `curl --location '${baseUrl}/refunds/ref_mbabizu24mvu3mela5njyhpit4' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: genericRefund,
      },
      'update-refund': {
        request: `curl --location '${baseUrl}/refunds/ref_mbabizu24mvu3mela5njyhpit4' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "metadata": {
      "ticket_id": "SUP-43"
    }
  }'`,
        response: genericRefund,
      },
      'create-payout': {
        request: `curl --location '${baseUrl}/payouts/create' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "amount": 5000,
    "currency": "USD",
    "profile_id": "${profileId}",
    "customer_id": "customer_123"
  }'`,
        response: genericPayout,
      },
      'retrieve-payout': {
        request: `curl --location '${baseUrl}/payouts/pout_mbabizu24mvu3mela5njyhpit4' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: genericPayout,
      },
      'update-payout': {
        request: `curl --location --request PUT '${baseUrl}/payouts/pout_mbabizu24mvu3mela5njyhpit4' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "metadata": {
      "batch_id": "batch_42"
    }
  }'`,
        response: genericPayout,
      },
      'confirm-payout': {
        request: `curl --location '${baseUrl}/payouts/pout_mbabizu24mvu3mela5njyhpit4/confirm' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: genericPayout,
      },
      'cancel-payout': {
        request: `curl --location '${baseUrl}/payouts/pout_mbabizu24mvu3mela5njyhpit4/cancel' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: `{
  "payout_id": "pout_mbabizu24mvu3mela5njyhpit4",
  "status": "cancelled"
}`,
      },
      'fulfill-payout': {
        request: `curl --location '${baseUrl}/payouts/pout_mbabizu24mvu3mela5njyhpit4/fulfill' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: genericPayout,
      },
      'create-confirm-subscription': {
        request: `curl --location '${baseUrl}/subscriptions' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "customer_id": "customer_123",
    "profile_id": "${profileId}",
    "plan_id": "plan_monthly"
  }'`,
        response: genericSubscription,
      },
      'create-subscription': {
        request: `curl --location '${baseUrl}/subscriptions/create' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "customer_id": "customer_123",
    "profile_id": "${profileId}",
    "plan_id": "plan_monthly"
  }'`,
        response: `{
  "subscription_id": "sub_123",
  "status": "created"
}`,
      },
      'list-subscription-items': {
        request: `curl --location '${baseUrl}/subscriptions/items?subscription_id=sub_123' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: `{
  "items": [
    {
      "item_id": "si_123",
      "subscription_id": "sub_123"
    }
  ]
}`,
      },
      'list-subscriptions': {
        request: `curl --location '${baseUrl}/subscriptions/list?limit=20&offset=0' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: `{
  "data": [
    {
      "subscription_id": "sub_123",
      "status": "active"
    }
  ]
}`,
      },
      'retrieve-subscription': {
        request: `curl --location '${baseUrl}/subscriptions/sub_123' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: genericSubscription,
      },
      'confirm-subscription': {
        request: `curl --location '${baseUrl}/subscriptions/sub_123/confirm' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: genericSubscription,
      },
      'update-subscription': {
        request: `curl --location --request PUT '${baseUrl}/subscriptions/sub_123/update' \\
  --header 'Content-Type: application/json' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>' \\
  --data-raw '{
    "metadata": {
      "seat_count": "12"
    }
  }'`,
        response: genericSubscription,
      },
      'pause-subscription': {
        request: `curl --location '${baseUrl}/subscriptions/sub_123/pause' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: `{
  "subscription_id": "sub_123",
  "status": "paused"
}`,
      },
      'resume-subscription': {
        request: `curl --location '${baseUrl}/subscriptions/sub_123/resume' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: genericSubscription,
      },
      'cancel-subscription': {
        request: `curl --location '${baseUrl}/subscriptions/sub_123/cancel' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: `{
  "subscription_id": "sub_123",
  "status": "cancelled"
}`,
      },
      'estimate-subscription': {
        request: `curl --location '${baseUrl}/subscriptions/estimate?customer_id=customer_123&plan_id=plan_monthly' \\
  --header 'Accept: application/json' \\
  --header 'api-key: <YOUR_END_MERCHANT_API_KEY>'`,
        response: `{
  "estimate": {
    "amount": 2999,
    "currency": "USD"
  }
}`,
      },
    };
  }, [baseUrl, profileId, snippets.paymentCreate]);

  const flowapayExamples = useMemo(() => buildFlowapayExamples(baseUrl), [baseUrl]);

  const selectedSection =
    surface.sections.find((section) => section.id === selectedSectionId) || surface.sections[0];
  const selectedEndpoint = selectedEndpointId
    ? surface.endpoints.find((endpoint) => endpoint.id === selectedEndpointId)
    : null;
  const selectedSectionEndpoints = surface.endpoints.filter((endpoint) => endpoint.sectionId === selectedSection.id);

  const scrollContentTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const selectSurface = (nextSurfaceId) => {
    if (nextSurfaceId === surface.id) return;
    setSurfaceId(nextSurfaceId);
    setSelectedSectionId(SURFACES[nextSurfaceId].defaultSectionId);
    setSelectedEndpointId(null);
    setExpandedSectionIds([]);
    scrollContentTop();
  };

  const selectSection = (sectionId) => {
    setSelectedSectionId(sectionId);
    setSelectedEndpointId(null);
    const section = surface.sections.find((s) => s.id === sectionId);
    if (section?.endpoints?.length) {
      setExpandedSectionIds((current) =>
        current.includes(sectionId) ? current : [...current, sectionId]
      );
    }
    scrollContentTop();
  };

  const selectEndpoint = (endpointId) => {
    const endpoint = surface.endpoints.find((item) => item.id === endpointId);
    if (!endpoint) return;

    setSelectedSectionId(endpoint.sectionId);
    setSelectedEndpointId(endpoint.id);
    setExpandedSectionIds((current) =>
      current.includes(endpoint.sectionId) ? current : [...current, endpoint.sectionId]
    );
    scrollContentTop();
  };

  const toggleSection = (sectionId) => {
    setExpandedSectionIds((current) =>
      current.includes(sectionId)
        ? current.filter((id) => id !== sectionId)
        : [...current, sectionId]
    );
  };

  const activeExamples = isFlowapay ? flowapayExamples : endpointExamples;

  let content;
  if (selectedEndpoint) {
    content = (
      <EndpointDetails
        endpoint={selectedEndpoint}
        example={activeExamples[selectedEndpoint.id]}
        onBack={() => selectSection(selectedEndpoint.sectionId)}
      />
    );
  } else if (isFlowapay) {
    content = (
      <FlowapaySectionContent
        section={selectedSection}
        endpoints={selectedSectionEndpoints}
        baseUrl={baseUrl}
        onSelectSection={selectSection}
        onSelectEndpoint={selectEndpoint}
      />
    );
  } else if (selectedSection.id === 'overview') {
    content = (
      <OverviewSection
        baseUrl={baseUrl}
        profileId={profileId}
        snippets={snippets}
        onSelectSection={selectSection}
      />
    );
  } else if (selectedSection.id === 'authentication') {
    content = <AuthenticationSection />;
  } else if (selectedSection.id === 'checkout') {
    content = <CheckoutSection snippets={snippets} />;
  } else if (selectedSection.id === 'webhooks') {
    content = <WebhooksSection />;
  } else {
    content = (
      <SectionOverview
        section={selectedSection}
        endpoints={selectedSectionEndpoints}
        onSelectEndpoint={selectEndpoint}
      />
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-medium text-cyan-800">
            <BookOpen className="h-3.5 w-3.5" />
            Developer documentation
          </div>
          <h1 className="text-2xl font-semibold text-slate-950">VEXGATE merchant integration guide</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">{surface.tagline}</p>
        </div>
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">API surface</p>
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm" role="tablist" aria-label="API surface">
            {Object.values(SURFACES).map((option) => (
              <button
                key={option.id}
                type="button"
                role="tab"
                aria-selected={surface.id === option.id}
                onClick={() => selectSurface(option.id)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  surface.id === option.id
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="mt-1.5 max-w-xs text-xs leading-5 text-slate-500">
            A profile speaks exactly one contract. Copying requests across surfaces will fail —
            pick the one your profile is on.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
        <DocumentationNav
          sections={surface.sections}
          selectedSectionId={selectedSectionId}
          selectedEndpointId={selectedEndpointId}
          expandedSectionIds={expandedSectionIds}
          onToggleSection={toggleSection}
          onSelectSection={selectSection}
          onSelectEndpoint={selectEndpoint}
        />
        <div className="min-w-0">{content}</div>
      </div>
    </div>
  );
}

/** Portal route: same body, wrapped in the merchant chrome and fed the real profile. */
export default function DocumentationPage() {
  const { user } = useAuth();

  return (
    <MerchantLayout currentPage="documentation">
      <DocumentationContent profileId={user?.profile_id} />
    </MerchantLayout>
  );
}
